/**
 * Facebook Pixel tracking utilities
 *
 * This module provides helper functions for tracking Facebook Pixel events
 * that mirror the backend Conversions API events. All events include
 * proper deduplication via eventId and enhanced user matching parameters.
 *
 * Key concepts:
 * - external_id: User identifier (sessionId) for cross-device tracking
 * - eventId: Unique identifier for each event instance for deduplication between browser and server
 */

import {getSessionId} from "./TrackingUtil";
import {lazyLoadFacebookPixel} from "./LazyLoadScripts";
import {v4 as uuidv4} from "uuid";

const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID;

// Meta standard events must be fired via `fbq('track', ...)` — firing them via
// `trackCustom` routes them into the `fb_pixel_custom` bucket instead of the
// proper standard-event action (e.g. `submit_application_website`), which breaks
// both Ads reporting and Pixel/CAPI deduplication.
const META_STANDARD_EVENTS = new Set([
    'AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration',
    'Contact', 'CustomizeProduct', 'Donate', 'FindLocation', 'InitiateCheckout',
    'Lead', 'PageView', 'Purchase', 'Schedule', 'Search', 'StartTrial',
    'SubmitApplication', 'Subscribe', 'ViewContent'
]);

// Re-init the pixel with advanced matching data. The react-facebook-pixel
// wrapper does not expose advanced matching on per-event calls — the only way
// to set em/ph/external_id for browser-side matching is via `fbq('init', ...)`.
// Meta supports re-init to refresh matching data for subsequent events.
function applyAdvancedMatching(pixel: any, userData: FacebookPixelUserData): void {
    if (!FB_PIXEL_ID) return;
    pixel.init(FB_PIXEL_ID, userData, {autoConfig: true, debug: false});
}

// Fires a Pixel event through `pixel.fbq(...)` directly so the options object
// (with eventID for CAPI dedup) actually reaches Meta. `pixel.track(title, data)`
// in react-facebook-pixel@1.0.4 drops any 3rd argument — passing eventID there
// was silently ignored. Uses `track` for standard events, `trackCustom` otherwise.
function trackPixelEvent(pixel: any, eventName: string, data: FacebookPixelEventData, eventId?: string): void {
    const method = META_STANDARD_EVENTS.has(eventName) ? 'track' : 'trackCustom';
    if (eventId) {
        pixel.fbq(method, eventName, data, {eventID: eventId});
    } else {
        pixel.fbq(method, eventName, data);
    }
}

/**
 * Base user data for Facebook Pixel events
 */
interface FacebookPixelUserData {
    em?: string;          // Email (will be hashed by Facebook Pixel automatically)
    ph?: string;          // Phone number (will be hashed by Facebook Pixel automatically)
    fn?: string;          // First name (lower-cased, single token; Pixel hashes automatically)
    external_id?: string; // External ID (sessionId when user not logged in)
}

/**
 * Standard event data that can be included with any event
 */
interface FacebookPixelEventData {
    value?: number;
    currency?: string;
    content_name?: string;
    content_ids?: string[];
    content_type?: string;

    [key: string]: any;
}

/**
 * Get the Facebook Pixel instance (lazy loaded)
 */
async function getPixel(): Promise<any> {
    try {
        const ReactPixel = await lazyLoadFacebookPixel();
        return ReactPixel;
    } catch (error) {
        console.error('Failed to load Facebook Pixel:', error);
        return null;
    }
}

/**
 * Build user data object with automatic enrichment
 * Always includes sessionId as external_id for user matching
 */
function buildUserData(email?: string, phone?: string, firstName?: string): FacebookPixelUserData {
    const userData: FacebookPixelUserData = {};

    if (email) {
        userData.em = email;
    }

    if (phone) {
        // Meta expects digits only with country code (e.g. "48123456789").
        // Frontend forms submit in `+<cc><digits>` format — stripping non-digits
        // yields Meta's canonical form and matches backend CAPI hashing.
        userData.ph = phone.replace(/\D/g, '');
    }

    if (firstName) {
        // Meta normalisation: take the first whitespace-delimited token, lowercase, trim.
        // Pixel hashes the value automatically. Match server-side normalisation in
        // facebookConversionsTracking.sendConversion so Pixel + CAPI events dedupe
        // and produce identical user_data hashes.
        const normalised = firstName.trim().split(/\s+/)[0]?.toLowerCase();
        if (normalised) {
            userData.fn = normalised;
        }
    }

    // ALWAYS use sessionId as external_id for cross-device user matching
    const sessionId = getSessionId();
    if (sessionId) {
        userData.external_id = sessionId;
    }

    return userData;
}

/**
 * Track SubmitApplication event (custom event matching backend CAPI)
 *
 * Use this when:
 * - User submits newsletter opt-in
 * - User provides phone after opt-in (send ANOTHER SubmitApplication event with enriched data)
 * - User creates SaaS account
 *
 * IMPORTANT: According to Meta's best practice for lead enrichment:
 * - Each SubmitApplication event should have a UNIQUE event_id
 * - Use the SAME external_id (sessionId) to link events to the same user
 * - When enriching with phone, send a NEW SubmitApplication event with NEW event_id
 * - Meta will automatically merge user_data and avoid double-counting
 *
 * SubmitApplication is a Meta standard event, so we use `track` (not `trackCustom`)
 * — this routes to the `submit_application_website` action type in Ads reporting
 * and enables proper deduplication with backend CAPI (same event_name + event_id).
 *
 * @param email User's email address
 * @param phone User's phone number (optional, but include if available)
 * @param value Optional estimated lead value
 * @returns The eventId used (for passing to backend)
 */
export async function trackSubmitApplication(
    email: string,
    phone?: string,
    value?: number,
    firstName?: string
): Promise<string> {
    try {
        const pixel = await getPixel();
        if (!pixel) return '';

        const userData = buildUserData(email, phone, firstName);
        const eventData: FacebookPixelEventData = {};

        if (value) {
            eventData.value = value;
            eventData.currency = 'PLN';
        }

        // ALWAYS generate a new unique eventId for each SubmitApplication event
        // Meta uses external_id (sessionId) to link events to the same user
        // and automatically enriches user_data without double-counting
        const eventId = uuidv4();

        applyAdvancedMatching(pixel, userData);
        trackPixelEvent(pixel, 'SubmitApplication', eventData, eventId);

        return eventId;
    } catch (error) {
        console.error('Failed to track SubmitApplication event:', error);
        return '';
    }
}

/**
 * Track CompleteRegistration event
 *
 * Use this when:
 * - User completes webinar watching
 * - User finishes account setup
 *
 * @param email User's email address
 * @param phone User's phone number (optional)
 * @param contentName Name of what was completed (e.g., "Webinar: Title")
 */
export async function trackCompleteRegistration(
    email: string,
    phone?: string,
    contentName?: string
): Promise<void> {
    try {
        const pixel = await getPixel();
        if (!pixel) return;

        const userData = buildUserData(email, phone);
        const eventData: FacebookPixelEventData = {};

        if (contentName) {
            eventData.content_name = contentName;
        }

        applyAdvancedMatching(pixel, userData);
        trackPixelEvent(pixel, 'CompleteRegistration', eventData);

        console.debug('[FB Pixel] CompleteRegistration tracked:', {email, contentName});
    } catch (error) {
        console.error('Failed to track CompleteRegistration event:', error);
    }
}

/**
 * Track custom webinar event
 *
 * Use this for:
 * - WebinarSawOffer - User watched until pitch (>90 minutes)
 * - WebinarLeftEarly - User left before pitch
 * - CompleteRegistration - User starts watching webinar
 *
 * @param eventName Custom event name
 * @param email User's email address
 * @param phone User's phone number (optional)
 * @param webinarTitle Title of the webinar
 * @param watchTimeMinutes How long user watched (optional)
 * @returns The eventId used (for passing to backend)
 */
export async function trackWebinarEvent(
    eventName: 'WebinarSawOffer' | 'WebinarLeftEarly' | 'CompleteRegistration' | string,
    email: string,
    phone?: string,
    webinarTitle?: string,
    watchTimeMinutes?: number,
    value?: number,
    firstName?: string
): Promise<string> {
    try {
        const pixel = await getPixel();
        if (!pixel) return '';

        const userData = buildUserData(email, phone, firstName);
        const eventData: FacebookPixelEventData = {
            content_name: webinarTitle || 'Webinar'
        };

        if (watchTimeMinutes !== undefined) {
            eventData.watch_time = watchTimeMinutes;
        }

        if (value) {
            eventData.value = value;
            eventData.currency = 'PLN';
        }

        // Generate unique eventId for deduplication with backend CAPI
        const eventId = uuidv4();

        applyAdvancedMatching(pixel, userData);
        // Routes standard events (e.g. CompleteRegistration) to `track` and
        // true custom events (WebinarSawOffer, WebinarLeftEarly) to `trackCustom`.
        trackPixelEvent(pixel, eventName, eventData, eventId);

        console.debug('[FB Pixel] Webinar event tracked:', {
            eventName,
            email,
            webinarTitle,
            eventId,
            sessionId: userData.external_id
        });

        return eventId;
    } catch (error) {
        console.error('Failed to track webinar event:', error);
        return '';
    }
}

/**
 * Track InitiateCheckout event
 *
 * Use this when:
 * - User clicks purchase button
 * - Checkout process begins
 *
 * @param email User's email address (optional - Facebook can still track via cookies and sessionId if not provided)
 * @param phone User's phone number (optional)
 * @param value Price of the product
 * @param currency Currency code (default: PLN)
 * @param contentIds Array of product IDs/SKUs
 * @param contentName Name of the product
 * @returns The unique eventId (for passing to backend)
 */
export async function trackInitiateCheckout(
    email: string | undefined,
    phone: string | undefined,
    value: number,
    currency: string = 'PLN',
    contentIds?: string[],
    contentName?: string
): Promise<string> {
    try {
        const pixel = await getPixel();
        if (!pixel) return '';

        const userData = buildUserData(email, phone);
        const eventData: FacebookPixelEventData = {
            value,
            currency,
            content_type: 'product'
        };

        if (contentIds && contentIds.length > 0) {
            eventData.content_ids = contentIds;
        }

        if (contentName) {
            eventData.content_name = contentName;
        }

        // Generate unique eventId for this checkout initiation
        const eventId = uuidv4();

        applyAdvancedMatching(pixel, userData);
        trackPixelEvent(pixel, 'InitiateCheckout', eventData, eventId);

        console.debug('[FB Pixel] InitiateCheckout tracked:', {
            email,
            value,
            currency,
            contentName,
            eventId
        });

        return eventId;
    } catch (error) {
        console.error('Failed to track InitiateCheckout event:', error);
        return '';
    }
}

/**
 * Track Purchase event
 *
 * Use this when:
 * - Payment is successfully completed
 * - Transaction is confirmed
 *
 * @param email User's email address (optional - Facebook can still track via cookies and sessionId if not provided)
 * @param phone User's phone number (optional)
 * @param value Purchase amount
 * @param currency Currency code (default: PLN)
 * @param transactionId Transaction ID (MUST be provided for proper deduplication with backend)
 * @param contentIds Array of product IDs/SKUs
 * @param contentName Name of the product
 */
export async function trackPurchase(
    email: string | undefined,
    phone: string | undefined,
    value: number,
    currency: string = 'PLN',
    transactionId: string,
    contentIds?: string[],
    contentName?: string
): Promise<void> {
    try {
        const pixel = await getPixel();
        if (!pixel) return;

        const userData = buildUserData(email, phone);
        const eventData: FacebookPixelEventData = {
            value,
            currency,
            content_type: 'product'
        };

        if (contentIds && contentIds.length > 0) {
            eventData.content_ids = contentIds;
        }

        if (contentName) {
            eventData.content_name = contentName;
        }

        // Use transaction ID as eventId for deduplication with backend CAPI
        applyAdvancedMatching(pixel, userData);
        trackPixelEvent(pixel, 'Purchase', eventData, transactionId);

        console.debug('[FB Pixel] Purchase tracked:', {
            email,
            value,
            currency,
            transactionId,
            contentName
        });
    } catch (error) {
        console.error('Failed to track Purchase event:', error);
    }
}

/**
 * Track ViewContent event
 *
 * Use this when:
 * - User starts watching webinar video
 * - User views important content
 *
 * @param contentName Name of the content being viewed
 * @param contentIds Array of content IDs
 * @param email User's email (optional)
 * @param phone User's phone (optional)
 */
export async function trackViewContent(
    contentName: string,
    contentIds?: string[],
    email?: string,
    phone?: string
): Promise<void> {
    try {
        const pixel = await getPixel();
        if (!pixel) return;

        const userData = buildUserData(email, phone);
        const eventData: FacebookPixelEventData = {
            content_name: contentName,
            content_type: 'video'
        };

        if (contentIds && contentIds.length > 0) {
            eventData.content_ids = contentIds;
        }

        applyAdvancedMatching(pixel, userData);
        trackPixelEvent(pixel, 'ViewContent', eventData);

        console.debug('[FB Pixel] ViewContent tracked:', {contentName});
    } catch (error) {
        console.error('Failed to track ViewContent event:', error);
    }
}

/**
 * Track AddToCart event
 *
 * Use this when:
 * - User adds product to cart (if applicable)
 * - User selects a purchase option
 *
 * @param email User's email address
 * @param phone User's phone number (optional)
 * @param value Price of the product
 * @param currency Currency code (default: PLN)
 * @param contentIds Array of product IDs/SKUs
 * @param contentName Name of the product
 */
export async function trackAddToCart(
    email: string,
    phone: string | undefined,
    value: number,
    currency: string = 'PLN',
    contentIds?: string[],
    contentName?: string
): Promise<void> {
    try {
        const pixel = await getPixel();
        if (!pixel) return;

        const userData = buildUserData(email, phone);
        const eventData: FacebookPixelEventData = {
            value,
            currency,
            content_type: 'product'
        };

        if (contentIds && contentIds.length > 0) {
            eventData.content_ids = contentIds;
        }

        if (contentName) {
            eventData.content_name = contentName;
        }

        applyAdvancedMatching(pixel, userData);
        trackPixelEvent(pixel, 'AddToCart', eventData);

        console.debug('[FB Pixel] AddToCart tracked:', {email, value, currency, contentName});
    } catch (error) {
        console.error('Failed to track AddToCart event:', error);
    }
}
